import { useCallback } from 'react';
import { type Location, useBeforeUnload, useBlocker } from 'react-router-dom';

export type NavigationConfirm = {
  isBlocked: boolean;
  confirm: () => void;
  cancel: () => void;
};

type NavigationArgs = {
  currentLocation: Location;
  nextLocation: Location;
};

/**
 * `isLeaving` narrows what counts as leaving: a wizard that keeps its step in the query string
 * navigates without the page changing, and that must not raise the confirmation.
 */
export const useNavigationConfirm = (
  shouldConfirm: boolean,
  isLeaving: (_args: NavigationArgs) => boolean = () => true,
): NavigationConfirm => {
  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (shouldConfirm) event.preventDefault();
      },
      [shouldConfirm],
    ),
    { capture: true },
  );

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldConfirm && isLeaving({ currentLocation, nextLocation }),
  );

  const confirm = useCallback(() => blocker.proceed?.(), [blocker]);
  const cancel = useCallback(() => blocker.reset?.(), [blocker]);

  return { isBlocked: blocker.state === 'blocked', confirm, cancel };
};
